import * as Sentry from '@sentry/react'
import type React from 'react'
import { createContext, type ReactNode, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Joyride, { type CallBackProps, type Step } from 'react-joyride'

import { useTourHomePage } from './components/TourAvail'
import { tourStorage } from './constants'
import TourProgressBar from './TourProgressBar'
import useTourRefineSearch from './TourRefineSearch'
import useTourReservation from './TourReservation'
import useTourResults from './TourResults'
import useTourSeeMoreTarifs from './TourSeeMoreTarifs'
import useTourVipSolicitation from './TourVipSolicitation'

interface TourSteps {
  selector: string
  content: ReactNode
}
type TourContextType = {
  startTour: (tourName: string) => void
  isTourOpen: boolean
  currentStep: number
}

const TourContext = createContext<TourContextType | undefined>(undefined)

export function useTourControl() {
  const ctx = useContext(TourContext)
  if (!ctx)
    throw new Error('useTourControl must be used within B2BTourProvider')
  return ctx
}
function convertSteps(steps: TourSteps[]): Step[] {
  return steps.map(step => ({
    target: step.selector || 'body',
    content: step.content,
    disableBeacon: true,
  }))
}

interface B2BTourProviderProps {
  children: React.ReactNode
}

const B2BTourProvider: React.FC<B2BTourProviderProps> = ({ children }) => {
  const { t } = useTranslation('tour')
  const [isTourOpen, setIsTourOpen] = useState(false)
  const [steps, setSteps] = useState<Step[]>([])
  const [startedTours, setStartedTours] = useState<string[]>([])
  const [finishedTours, setFinishedTours] = useState<string[]>([])
  const [currentStep, setCurrentStep] = useState(0)
  const homeSteps = useTourHomePage()
  const resultSteps = useTourResults()
  const seeMoreTarifsSteps = useTourSeeMoreTarifs()
  const refineSearchSteps = useTourRefineSearch()
  const vipSolicitationSteps = useTourVipSolicitation()
  const tourReservationSteps = useTourReservation()
  const [currentTourName, setCurrentTourName] = useState<string>('')

  function prepareTour(tourName: string) {
    setStartedTours(prev => [...prev, tourName])
    setCurrentTourName(tourName)
    Sentry.captureMessage(`Tour iniciado: ${tourName}`, {
      level: 'info',
      tags: { tour: tourName },
      extra: { startedTours: [...startedTours, tourName] },
    })

    switch (tourName) {
      case 'home':
        return homeSteps
      case 'results':
        return resultSteps
      case 'seeMoreTarifs':
        return seeMoreTarifsSteps
      case 'refineSearch':
        return refineSearchSteps
      case 'vipSolicitation':
        return vipSolicitationSteps
      case 'tourReservation':
        return tourReservationSteps
      default:
        return []
    }
  }

  function setTourSeenByName(tourName: string) {
    setFinishedTours(prev => {
      if (prev.includes(tourName)) return prev
      const updated = [...prev, tourName]

      Sentry.captureMessage(`Tour finalizado: ${tourName}`, {
        level: 'info',
        tags: { tour: tourName },
        extra: { finishedTours: [...finishedTours, tourName] },
      })

      switch (tourName) {
        case 'home':
          return updated
        case 'results':
          tourStorage.setResultsSeen()
          return updated
        case 'seeMoreTarifs':
          tourStorage.setTarifsSeen()
          return updated
        case 'refineSearch':
          tourStorage.setRefineSearchTourSeen()
          return updated
        case 'vipSolicitation':
          tourStorage.setVipSolicitationSeen()
          return updated
        case 'tourReservation':
          tourStorage.setTourReservationSeen()
          return updated
        default:
          return updated
      }
    })
  }
  const startTour = (tourName: string) => {
    const steps = prepareTour(tourName)
    setIsTourOpen(false)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setSteps(convertSteps(steps))
        setIsTourOpen(true)
      })
    })
  }

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, index } = data
    if (typeof index === 'number') {
      setCurrentStep(index)
    }
    if (status === 'finished' && currentTourName) {
      setTourSeenByName(currentTourName)
      setCurrentTourName('')
      setIsTourOpen(false)
      setCurrentStep(0)
      return
    }
    if (['skipped', 'close'].includes(status)) {
      setIsTourOpen(false)
      setCurrentStep(0)
      setCurrentTourName('')
    }
  }

  return (
    <TourContext.Provider
      value={{
        isTourOpen,
        currentStep,
        startTour,
      }}
    >
      <Joyride
        hideCloseButton
        steps={steps}
        run={isTourOpen}
        callback={handleJoyrideCallback}
        continuous
        showSkipButton
        showProgress={false}
        disableScrolling={false}
        styles={{
          options: {
            zIndex: 20000,
            primaryColor: '#1B144B',
          },
        }}
        locale={{
          back: t('tour.steps.back'),
          close: t('tour.steps.close'),
          last: t('tour.steps.last'),
          next: t('tour.steps.next'),
          skip: t('tour.steps.skip'),
        }}
        disableScrollParentFix
      />
      {isTourOpen && (
        <TourProgressBar steps={steps} currentStep={currentStep} />
      )}
      {children}
    </TourContext.Provider>
  )
}

export default B2BTourProvider
